import { createContext, useCallback, useContext, type ReactNode } from 'react'
import { useAuth } from '../auth/useAuth'
import { useCurrentUser } from '../auth/useCurrentUser'

/**
 * The area (district) every statistics request is scoped to.
 *
 * The area switcher in the top bar belongs to clubhouse, which this package may not
 * import, so the app shell hands its active area in through `buildStatisticsRoutes`
 * and `ActiveDistrictProvider` makes it available here. Statistics used to send the
 * first admin membership on their own, so a user with several areas saw the same
 * area's figures whichever one they chose.
 */
type ActiveDistrictSource = () => string | null

// undefined: no shell source is mounted (a page rendered on its own, as in tests).
const ActiveDistrictContext = createContext<string | null | undefined>(undefined)

export function ActiveDistrictProvider({
  useActiveDistrictId,
  children,
}: {
  /** A hook returning the active area id; called on every render of the provider. */
  useActiveDistrictId: ActiveDistrictSource
  children: ReactNode
}) {
  const districtId = useActiveDistrictId()
  return <ActiveDistrictContext.Provider value={districtId}>{children}</ActiveDistrictContext.Provider>
}

export interface StatisticsDistrict {
  /**
   * Sent as X-District-Id. Null when no shell supplies an area: the backend's
   * resolver then picks the first admin-or-higher membership itself, so the client
   * does not repeat that rule.
   */
  districtId: string | null
  /** Whether queries may run: signed in, and the area settled (see `waiting`). */
  enabled: boolean
  /**
   * True while the shell's area is not yet one of the signed-in user's areas: before
   * the switcher has resolved one, or while it still holds the previous identity's
   * area after a sign-out or an impersonation (its store outlives both). Asking then
   * would get the backend's fallback area — another area's figures — or a 403 for an
   * area the user may not read. Pages treat it as loading: a disabled query reads as
   * not loading, which made every tab announce that there was no active season.
   */
  waiting: boolean
  /**
   * A query key scoped to the area and to this package. The area in the key makes a
   * switch fetch afresh instead of showing the previous area's cached figures, and the
   * prefix keeps clubhouse's queries on the same client from sharing an entry with
   * these — they once did, and a team list from one area named another area's teams.
   */
  key: (...parts: readonly unknown[]) => readonly unknown[]
}

export function useStatisticsDistrict(): StatisticsDistrict {
  const { state } = useAuth()
  const fromShell = useContext(ActiveDistrictContext)
  const { data: user } = useCurrentUser()

  const signedIn = state.status === 'signed-in'
  const districtId = fromShell ?? null
  const memberships = Array.isArray(user?.memberships) ? user.memberships : []
  const settled =
    fromShell === undefined ||
    (user != null &&
      (memberships.length === 0 || memberships.some((m) => m.districtId === fromShell)))

  const key = useCallback(
    (...parts: readonly unknown[]) => ['chronicle', districtId, ...parts] as const,
    [districtId],
  )
  return { districtId, enabled: signedIn && settled, waiting: signedIn && !settled, key }
}
